import React from 'react';
import PropTypes from 'prop-types';
import { LuExternalLink, LuFileSearch, LuImage, LuImageOff } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useErrorReportText from '../errorReportText';
import { StatusBadge } from './reportBits';

const BLOCK = 'tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]';
const BLOCK_TITLE = 'tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

/** The screenshot of a report, as large as the screen allows. */
export const ScreenshotDialog = ({ url, onClose }) => {
  const { label, language } = useErrorReportText();
  return (
    <Modal
      isOpen={Boolean(url)}
      onClose={onClose}
      size="xl"
      title={label('screenshot.title')}
      closeLabel={label('screenshot.close')}
      lang={language}
      bodyClassName="tw-p-3 sm:tw-p-5"
      icon={(
        <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
          <LuImage aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-mr-auto tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-teal-700 hover:tw-text-teal-800 dark:tw-text-teal-300 dark:hover:tw-text-teal-200"
            >
              <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('screenshot.openInTab')}
            </a>
          )}
          <Button onClick={onClose}>{label('screenshot.close')}</Button>
        </>
      )}
    >
      {url ? (
        <div className="tw-grid tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-p-2 dark:tw-bg-black/30">
          <img src={url} alt={label('screenshot.alt')} className="tw-max-h-[calc(88vh-13rem)] tw-w-auto tw-max-w-full tw-rounded-xl tw-object-contain" />
        </div>
      ) : (
        <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-500"><LuImageOff aria-hidden="true" className="tw-h-4 tw-w-4" />{label('screenshot.missing')}</p>
      )}
    </Modal>
  );
};

ScreenshotDialog.propTypes = { url: PropTypes.string, onClose: PropTypes.func.isRequired };

const Field = ({ name, children }) => (
  <p className="tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">
    <span className="tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{name}: </span>
    {children}
  </p>
);

Field.propTypes = { name: PropTypes.string.isRequired, children: PropTypes.node };

/** Everything about one report (the old "Hata Detayları" window). */
export const ReportDetailsDialog = ({ report, isOpen, onClose, onViewScreenshot }) => {
  const { label, language, formatDate, fieldText } = useErrorReportText();
  const empty = <span className="tw-text-slate-400 dark:tw-text-slate-500">{label('details.noInfo')}</span>;
  return (
    <Modal
      isOpen={isOpen && Boolean(report)}
      onClose={onClose}
      size="lg"
      title={label('details.title')}
      subtitle={report?.userName}
      closeLabel={label('details.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" size="xs">
          <LuFileSearch aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button onClick={onClose}>{label('details.close')}</Button>}
    >
      {report && (
        <div className="tw-space-y-3">
          <section className={BLOCK}>
            <p className={BLOCK_TITLE}>{label('details.summary')}</p>
            <p className="tw-mt-2 tw-whitespace-pre-wrap tw-break-words tw-text-[15px] tw-leading-relaxed tw-text-slate-800 dark:tw-text-slate-100">
              {report.errorSummary || label('table.noSummary')}
            </p>
          </section>

          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <section className={BLOCK}>
              <p className={BLOCK_TITLE}>{label('details.user')}</p>
              <div className="tw-mt-2 tw-space-y-1">
                <Field name={label('details.name')}>{report.userName}</Field>
                <Field name={label('details.id')}><span className="tw-break-all tw-font-mono tw-text-xs">{report.uid}</span></Field>
                <Field name={label('details.date')}>{formatDate(report.timestamp)}</Field>
              </div>
            </section>
            <section className={BLOCK}>
              <p className={BLOCK_TITLE}>{label('details.status')}</p>
              <div className="tw-mt-2 tw-space-y-1.5">
                <StatusBadge status={report.status} />
                {report.statusUpdatedByName && (
                  <>
                    <Field name={label('details.updatedBy')}>{report.statusUpdatedByName}</Field>
                    <Field name={label('details.updatedAt')}>{formatDate(report.statusUpdatedAt)}</Field>
                  </>
                )}
              </div>
            </section>
          </div>

          <section className={BLOCK}>
            <p className={BLOCK_TITLE}>{label('details.system')}</p>
            <div className="tw-mt-2 tw-space-y-1">
              <Field name={label('details.device')}>{fieldText(report, 'deviceInfo') || empty}</Field>
              <Field name={label('details.browser')}>{fieldText(report, 'browserInfo') || empty}</Field>
            </div>
          </section>

          <section className={BLOCK}>
            <p className={BLOCK_TITLE}>{label('details.detailed')}</p>
            {fieldText(report, 'errorDetails') ? (
              <pre className="tw-mt-2 tw-max-h-64 tw-overflow-auto tw-whitespace-pre-wrap tw-break-words tw-rounded-xl tw-bg-slate-900/[0.04] tw-p-3 tw-font-mono tw-text-xs tw-text-slate-700 dark:tw-bg-black/30 dark:tw-text-slate-200">
                {report.errorDetails}
              </pre>
            ) : (
              <p className="tw-mt-2 tw-text-sm">{empty}</p>
            )}
          </section>

          {report.screenshotUrl && (
            <section className={BLOCK}>
              <p className={BLOCK_TITLE}>{label('details.screenshot')}</p>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onViewScreenshot(report.screenshotUrl);
                }}
                title={label('panel.screenshotHint')}
                className="tw-mt-2 tw-block tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-border-white/10"
              >
                <img src={report.screenshotUrl} alt={label('screenshot.alt')} loading="lazy" className="tw-h-40 tw-w-auto tw-max-w-full tw-object-cover" />
              </button>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
};

ReportDetailsDialog.propTypes = {
  report: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onViewScreenshot: PropTypes.func.isRequired,
};
